import { useEffect, useRef, useState } from 'react';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { FieldProps } from '@rjsf/core';

import { BeyondTrustIcon, VaultIcon } from '@attackiq/icons';
import {
  CredentialGlobalProperty,
  ScenarioGlobalPropertyWithValue,
  useCredentialGlobalProperties,
  useScenarioGlobalProperty
} from '@attackiq/queries';

import type { AiReactJsonSchemaFormContext } from '../AiReactJsonSchemaForm';

const BEYONDTRUST_TITLES: Record<string, string> = {
  'beyondtrust/password_safe': 'BeyondTrust Password Safe ™',
  'beyondtrust/secrets_safe': 'BeyondTrust Secrets Safe ™'
};

const CREDENTIAL_INPUT_FIELDS = ['username', 'domain', 'password'] as const;
type CredentialInputField = (typeof CREDENTIAL_INPUT_FIELDS)[number];

const CREDENTIAL_INPUT_LABELS: Record<CredentialInputField, string> = {
  username: 'Username',
  domain: 'Domain (optional)',
  password: 'Password'
};

type DisplayValues = Record<CredentialInputField, string>;

const EMPTY_DISPLAY_VALUES: DisplayValues = { username: '', domain: '', password: '' };

interface CredentialsValue {
  global_property_id?: string;
}

const extractGpValue = (gp: ScenarioGlobalPropertyWithValue, key: string): string => {
  if (Array.isArray(gp.value)) return '';
  const raw = gp.value[`${gp.aiq_type}.${key}`];
  if (raw == null) return '';
  return typeof raw === 'object' ? String(raw.value ?? '') : String(raw);
};

const GpOriginBadge = ({ origin }: { origin?: string | null }) => {
  if (!origin) return null;
  if (['vault/kv_v1', 'vault/kv_v2', 'vault/aws'].includes(origin))
    return (
      <Tooltip arrow placement="top" title="HashiCorp Vault ™">
        <Box display="flex">
          <VaultIcon />
        </Box>
      </Tooltip>
    );
  const btTitle = BEYONDTRUST_TITLES[origin];
  if (btTitle)
    return (
      <Tooltip arrow placement="top" title={btTitle}>
        <Box display="flex">
          <BeyondTrustIcon />
        </Box>
      </Tooltip>
    );
  if (['cyberark/cyberark', 'cyberark/cyberark_aws', 'cyberark/cyberark_azure'].includes(origin))
    return (
      <Typography variant="caption" color="text.secondary">
        CyberArk ™
      </Typography>
    );
  return null;
};

const parseParentPath = (id: string): (string | number)[] =>
  id
    .replace(/^root_/, '')
    .replace(/_credentials$/, '')
    .split('_')
    .map(segment => (/^\d+$/.test(segment) ? Number(segment) : segment));

const readStringAtPath = (root: unknown, path: (string | number)[]): string => {
  const value = path.reduce<unknown>((acc, segment) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string | number, unknown>)[segment];
  }, root);
  return typeof value === 'string' ? value : '';
};

export const CommandCredentialPickerField = (props: FieldProps<CredentialsValue>) => {
  const { formData, onChange, idSchema, formContext } = props;

  const ctx = formContext as AiReactJsonSchemaFormContext | undefined;
  const patchFormDataAtPath = ctx?.patchFormDataAtPath;
  const allFormData = ctx?.formData;

  const parentPath = parseParentPath(idSchema.$id);

  const [localInputs, setLocalInputs] = useState<Partial<DisplayValues>>({});

  const boundGpId = formData?.global_property_id;

  const { data: gpList = [], isLoading: isLoadingGpList } = useCredentialGlobalProperties();
  const selectedGp = boundGpId ? (gpList.find(gp => gp.id === boundGpId) ?? null) : null;
  const selectedGpId = selectedGp?.id;
  const selectedGpName = selectedGp?.name;

  const {
    data: gpDetails,
    isFetching: isFetchingGpDetails,
    isError: fetchError
  } = useScenarioGlobalProperty(selectedGpId);

  const gpDisplayValues: DisplayValues =
    !selectedGp || !gpDetails
      ? EMPTY_DISPLAY_VALUES
      : {
          username: extractGpValue(gpDetails, 'username'),
          domain: '',
          password: selectedGpName ?? ''
        };

  const lastFannedRef = useRef<string | undefined>(undefined);
  const pickInProgressRef = useRef(false);

  useEffect(() => {
    if (!selectedGp || !gpDetails) {
      lastFannedRef.current = undefined;
      return;
    }
    if (lastFannedRef.current === selectedGp.id) return;
    lastFannedRef.current = selectedGp.id;
    patchFormDataAtPath?.([...parentPath, 'username'], extractGpValue(gpDetails, 'username'));
    patchFormDataAtPath?.([...parentPath, 'domain'], '');
    patchFormDataAtPath?.([...parentPath, 'password'], selectedGp.name ?? '');
  }, [selectedGp, gpDetails, patchFormDataAtPath, parentPath]);

  useEffect(() => {
    pickInProgressRef.current = false;
  });

  const selectGp = (gp: CredentialGlobalProperty) => {
    pickInProgressRef.current = true;
    setLocalInputs({});
    onChange({ global_property_id: gp.id });
  };

  const recordTyping = (field: CredentialInputField, value: string) => {
    setLocalInputs(prev => ({ ...prev, [field]: value }));
  };

  const commitField = (field: CredentialInputField) => {
    if (pickInProgressRef.current) {
      setLocalInputs(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      return;
    }
    const typed = localInputs[field];
    if (typed === undefined) return;
    const committed = readStringAtPath(allFormData, [...parentPath, field]);
    if (selectedGp && typed !== gpDisplayValues[field]) {
      patchFormDataAtPath?.([...parentPath, 'credentials'], undefined);
    }
    if (typed !== committed) {
      patchFormDataAtPath?.([...parentPath, field], typed);
    }
    setLocalInputs(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const inputValueFor = (field: CredentialInputField): string => {
    const typed = localInputs[field];
    if (typed !== undefined) return typed;
    if (selectedGp) return gpDisplayValues[field];
    return readStringAtPath(allFormData, [...parentPath, field]);
  };

  const renderField = (field: CredentialInputField) => (
    <Autocomplete<CredentialGlobalProperty, false, false, true>
      key={field}
      value={null}
      inputValue={inputValueFor(field)}
      options={gpList}
      loading={isLoadingGpList || isFetchingGpDetails}
      freeSolo
      handleHomeEndKeys
      getOptionLabel={gp => (typeof gp === 'string' ? gp : (gp?.name ?? ''))}
      getOptionKey={gp => (typeof gp === 'string' ? gp : gp.id)}
      isOptionEqualToValue={(a, b) => typeof b !== 'string' && b != null && a.id === b.id}
      filterOptions={(options, state) => {
        const query = state.inputValue.trim().toLowerCase();
        return query ? options.filter(option => option.name.toLowerCase().includes(query)) : options;
      }}
      onInputChange={(_, value, reason) => {
        if (reason === 'input' || reason === 'clear') recordTyping(field, value);
      }}
      onChange={(_, gp) => {
        if (gp == null || typeof gp === 'string') return;
        selectGp(gp);
      }}
      onBlur={() => commitField(field)}
      renderOption={(renderProps, option) => {
        const { key, ...optionProps } = renderProps as typeof renderProps & { key: React.Key };
        return (
          <Box
            key={key}
            component="li"
            {...optionProps}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <span>{option.name}</span>
            <GpOriginBadge origin={option.origin} />
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          id={`${idSchema.$id}_${field}`}
          label={CREDENTIAL_INPUT_LABELS[field]}
          type={field === 'password' ? 'password' : 'text'}
          autoComplete={field === 'password' ? 'new-password' : 'off'}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedGp ? (
              <InputAdornment position="start">
                <VpnKeyOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ) : undefined,
            endAdornment: (
              <>
                {(isLoadingGpList || isFetchingGpDetails) && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {CREDENTIAL_INPUT_FIELDS.map(renderField)}
      {fetchError && (
        <Typography variant="caption" color="error">
          Failed to load credential details.
        </Typography>
      )}
      {selectedGp && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {selectedGp.origin && selectedGp.origin !== 'manual' ? (
            <GpOriginBadge origin={selectedGp.origin} />
          ) : (
            <VpnKeyOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {selectedGp.name}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
